import React, { useCallback, useMemo, useState } from "react";

import {
  IGuestlistTicketAndPass,
  IGuestlistTicketAndPassParams,
} from "Bands/types";
import { copy } from "shared/assets";
import { Card, Title } from "shared/components";
import { Spacer } from "shared/layout";
import { ICardTheme } from "shared/types";
import {
  TicketAndPassTable,
  DeleteTicketAndPassModal,
  CreateTicketAndPassForm,
} from "Bands/containers";
import { EditTicketAndPass } from "./EditTicketAndPass";
import { useBandContext } from "Bands/contexts";

const ticketAndPassCopy = copy.guestlistBlock.ticketAndPasses.create;

interface EditTicketAndPassesProps {
  blockId: string;
  ticketAndPasses: IGuestlistTicketAndPass[];
  isGuestlistClosed?: boolean;
}

export const EditTicketAndPasses: React.FC<EditTicketAndPassesProps> = ({
  blockId,
  ticketAndPasses,
  isGuestlistClosed,
}) => {
  const [selectedTicketAndPass, setSelectedTicketAndPass] =
    useState<IGuestlistTicketAndPass | null>(null);

  const { ticketAndPassTypes } = useBandContext();

  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const findTicketAndPass = useCallback(
    (findId: string) => ticketAndPasses.find(({ id }) => id === findId),
    [ticketAndPasses]
  );

  const openEditModal = (ticketAndPass: IGuestlistTicketAndPassParams) => {
    setShowEditModal(true);

    const found = ticketAndPass.id ? findTicketAndPass(ticketAndPass.id) : null;

    if (found) {
      setSelectedTicketAndPass(found);
    }
  };

  const openDeleteModal = (deleteId: string) => {
    setShowDeleteModal(true);

    const found = findTicketAndPass(deleteId);

    if (found) {
      setSelectedTicketAndPass(found);
    }
  };

  const ticketAndPassesData = useMemo(
    () =>
      ticketAndPasses
        .map(({ ticketAndPassType, ...rest }) => ({
          ...rest,
          ticketAndPassType: ticketAndPassType.id,
        }))
        .sort((a, b) => {
          const aIndex = ticketAndPassTypes.findIndex(
            (type) => type.id === a.ticketAndPassType
          );
          const bIndex = ticketAndPassTypes.findIndex(
            (type) => type.id === b.ticketAndPassType
          );

          return aIndex - bIndex;
        }),
    [ticketAndPasses, ticketAndPassTypes]
  );

  return (
    <Card theme={ICardTheme.secondary}>
      {showEditModal && !!selectedTicketAndPass && (
        <EditTicketAndPass
          blockId={blockId}
          ticketAndPass={selectedTicketAndPass}
          handleClose={() => setShowEditModal(false)}
        />
      )}
      {showDeleteModal && selectedTicketAndPass && (
        <DeleteTicketAndPassModal
          ticketAndPass={selectedTicketAndPass}
          blockId={blockId}
          handleClose={() => setShowDeleteModal(false)}
        />
      )}
      <Spacer size="m">
        <Title size="m" asComponent="h3">
          {ticketAndPassCopy.title}
        </Title>

        {!isGuestlistClosed && (
          <CreateTicketAndPassForm
            blockId={blockId}
            addedTicketAndPasses={ticketAndPasses}
          />
        )}

        <TicketAndPassTable
          ticketAndPasses={ticketAndPassesData}
          handleEdit={openEditModal}
          handleDelete={openDeleteModal}
          hideActions={isGuestlistClosed}
        />
      </Spacer>
    </Card>
  );
};
